import React from 'react';
import { Dialog, RenderIf, Snackbar } from '@lib/ui-components';
import { FinancialReportType } from '@lib/api-interface';
import { useTranslation } from 'react-i18next';
import { useSseState } from '../state/sse-state';
import { useSseEvent } from './sse/sse-connection';
import { DialogContent, DialogContentText, DialogTitle, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

export interface ReportGenerationMessageModel {
  id: string;
  message: string;
  fileName?: string;
}

export interface GeneratedReportMessageModel {
  id: string;
  message: string;
  reportType: FinancialReportType;
  fileName?: string;
  timestamp: Date;
}

export interface BranchesImportMessageModel {
  id: string;
  message: string;
  created: number;
  updated: number;
  errorRows: { [key: number]: string };
  isExcelImportError: boolean;
  isIOError: boolean;
}

export interface BaseMessageModel {
  id: string;
  message: string;
  isErrorMessage?: boolean;
}

export interface ReceivedQuantitiesImportMessageModel {
  isError: boolean;
  rowNumber?: number;
  columnAddress?: string;
}

export function SseNotificationsService() {
  const { t } = useTranslation();
  const sseState = useSseState();
  const [reportGenerationModel, setReportGenerationModel] = React.useState<ReportGenerationMessageModel>();
  const [generatedReportModel, setGeneratedReportModel] = React.useState<GeneratedReportMessageModel>();
  const [branchesImportModel, setBranchesImportModel] = React.useState<BranchesImportMessageModel>();
  const [baseModel, setBaseModel] = React.useState<BaseMessageModel>();
  const [receivedQuantitiesImportMessageModel, setReceivedQuantitiesImportMessageModel] = React.useState<ReceivedQuantitiesImportMessageModel>();

  function resolveResponseForModel() {
    if (reportGenerationModel !== undefined) {
      const notificationMessage = t('reportGenerationStarted') + ': ' + reportGenerationModel.fileName;
      return <Snackbar message={notificationMessage} severity='info' onlyManuallyClosed={false} onClose={() => setReportGenerationModel(undefined)} />;
    }
    if (branchesImportModel !== undefined) {
      if (branchesImportModel.isIOError) {
        const notificationMessage = t('branchesImportIOError');
        return <Snackbar message={notificationMessage} severity='error' onlyManuallyClosed={true} onClose={() => setBranchesImportModel(undefined)} />;
      } else {
        return <BranchesImportDialog branchesImportModel={branchesImportModel} onClose={() => setBranchesImportModel(undefined)} />;
      }
    }
    if (generatedReportModel !== undefined) {
      if (generatedReportModel.fileName) {
        const notificationMessage = generatedReportModel.fileName + ' ' + t('reportGenerated');
        return <Snackbar message={notificationMessage} severity='success' onlyManuallyClosed={true} onClose={() => setGeneratedReportModel(undefined)} />;
      }
      const notificationMessage = t('reportNotGeneratedMessage');
      return <Snackbar message={notificationMessage} severity='error' onlyManuallyClosed={true} onClose={() => setGeneratedReportModel(undefined)} />;
    }
    if (baseModel !== undefined) {
      return (
        <Snackbar
          message={baseModel.isErrorMessage ? t('settlementExportError') : t('settlementImportFinished')}
          severity={baseModel.isErrorMessage ? 'error' : 'info'}
          onlyManuallyClosed={true}
          onClose={() => setBaseModel(undefined)}
        />
      );
    }
    if (receivedQuantitiesImportMessageModel !== undefined) {
      if (receivedQuantitiesImportMessageModel.isError) {
        let notificationMessage;
        if (receivedQuantitiesImportMessageModel.rowNumber && receivedQuantitiesImportMessageModel.columnAddress) {
          notificationMessage = t('receivedQuantitiesImport.errorWithRowColumn', {
            rowNumber: receivedQuantitiesImportMessageModel.rowNumber,
            columnAddress: receivedQuantitiesImportMessageModel.columnAddress,
          });
        } else if (receivedQuantitiesImportMessageModel.rowNumber) {
          notificationMessage = t('receivedQuantitiesImport.errorWithRow', {
            rowNumber: receivedQuantitiesImportMessageModel.rowNumber,
          });
        } else {
          notificationMessage = t('receivedQuantitiesImport.error');
        }
        return <Snackbar message={notificationMessage} severity='error' onlyManuallyClosed={true} onClose={() => setReceivedQuantitiesImportMessageModel(undefined)} />;
      }
      const notificationMessage = t('receivedQuantitiesImport.success');
      return <Snackbar message={notificationMessage} severity='success' onlyManuallyClosed={true} onClose={() => setReceivedQuantitiesImportMessageModel(undefined)} />;
    }
    return <></>;
  }

  const notification = React.useMemo(() => {
    return resolveResponseForModel();
  }, [reportGenerationModel, generatedReportModel, branchesImportModel, baseModel, receivedQuantitiesImportMessageModel, t]);
  // The refresh keys are bumped here, where the event arrives, and not while rendering the notification. They used to
  // be set inside resolveResponseForModel, which has one return per model: a report that finished while the
  // "generation started" snackbar was still on screen returned at the first branch, so the table never learnt to
  // reload and the row's spinner turned until someone pressed F5. Whether a snackbar is displayable has nothing to do
  // with whether the data behind it changed.
  useSseEvent<ReportGenerationMessageModel>('report-generation-started', (payload) => {
    sseState.setReportGenerationStartedRefreshKey();
    setReportGenerationModel({ id: payload.id, message: payload.message, fileName: payload.fileName });
  });

  useSseEvent<any>('report-generated', (payload) => {
    // Fires for a finished report and for one that produced nothing: NO_DATA and FAILED arrive on this event too,
    // carrying no fileName. The table has to reload for all three - the row's button reads generationStatus.
    sseState.setReportGeneratedRefreshKey();
    setGeneratedReportModel({
      id: payload.id,
      message: payload.message,
      reportType: payload.reportType,
      fileName: payload.fileName,
      timestamp: new Date(payload.timestamp),
    });
  });

  useSseEvent<BaseMessageModel>('settlement-import', (payload) => {
    sseState.setSettlementImportRefreshKey();
    setBaseModel({ id: payload.id, message: payload.message });
  });

  useSseEvent<BaseMessageModel>('settlement-export', (payload) => {
    // Its own key, not the import one. service-settlement-export.tsx has always waited on settlementExportRefreshKey
    // and nothing ever set it, so that screen never reloaded on its own either.
    sseState.setSettlementExportRefreshKey();
    setBaseModel({ id: payload.id, message: payload.message });
  });

  useSseEvent<any>('branches-import', (payload) => {
    sseState.setBranchesImportedRefreshKey();
    setBranchesImportModel({
      id: payload.id,
      message: payload.message,
      created: payload.created,
      updated: payload.updated,
      errorRows: payload.errorRows,
      // The wire names come from the getters Jackson derives: isExcelImportError -> excelImportError, isIOError -> ioerror.
      isExcelImportError: payload.excelImportError,
      isIOError: payload.ioerror,
    });
  });

  useSseEvent<any>('received-quantities-import', (payload) => {
    sseState.setRefreshReceivedQuantitiesImport();
    // A success payload carries no rowNumber; that absence is the only thing separating the two shapes.
    setReceivedQuantitiesImportMessageModel('rowNumber' in payload ? { isError: true, rowNumber: payload.rowNumber, columnAddress: payload.columnAddress } : { isError: false });
  });

  return notification;
}

function BranchesImportDialog({ branchesImportModel, onClose }: { branchesImportModel: BranchesImportMessageModel; onClose: () => void }) {
  const { t } = useTranslation();
  const createdCount = branchesImportModel.created;
  const updatedCount = branchesImportModel.updated;
  const errorRows = branchesImportModel.errorRows;
  const notificationMessage = t('branchesImported.content', { createdCount, updatedCount });

  return (
    <Dialog open={true} maxWidth='md' fullWidth={true} onClose={onClose}>
      <DialogTitle>
        {t('branchesImported.title')}
        <IconButton
          aria-label='close'
          onClick={onClose}
          data-test-id={'dialog-close-btn'}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>{notificationMessage}</DialogContentText>
        <RenderIf true={errorRows && Object.keys(errorRows).length > 0}>
          <DialogContentText>{t('branchesImported.errorRowsInfo')}</DialogContentText>
          {Object.entries(errorRows).map(([row, errorMessage]: [string, string], index: number) => (
            <DialogContentText key={index}>{`${row}: ${errorMessage}`}</DialogContentText>
          ))}
        </RenderIf>
      </DialogContent>
    </Dialog>
  );
}
